import React, { useMemo, useState } from 'react';
import { Plus, X, Eye } from 'lucide-react';
import { useUserTypes } from '../hooks/useUserTypes';

/**
 * Per-role pricing matrix editor (SAM-195). New Tailwind component per the
 * BaseUI/Styletron freeze (SAM-175) — mounted from ServiceDetailDrawer.tsx
 * (a grandfathered Styletron file) with a minimal touch, not merged into it.
 *
 * Rows = booking options (guest tiers, already edited above this section).
 * Columns = roles (user_type_id), fetched from /user_type — never hardcoded,
 * since roles are edited freely in Settings -> Roles and must not require a
 * code change to gain or lose a price column.
 */

export interface PricingMatrixOptionRow {
  /** Index into formData.booking_options, so edits can be applied back. */
  index: number;
  id: string;
  label: string;
  /** Flat price, shown as the fallback column and in the role preview. */
  flatPrice: string;
  pricesByRole: Record<string, string>;
}

export interface RoleCapsForm {
  max_qty_per_booking: string;
  max_qty_per_day: string;
}

interface ServicePricingMatrixProps {
  options: PricingMatrixOptionRow[];
  activeRoleIds: string[];
  defaultUserTypeId: string;
  roleCaps: Record<string, RoleCapsForm>;
  onCellChange: (optionIndex: number, userTypeId: string, value: string) => void;
  onAddRole: (userTypeId: string) => void;
  onRemoveRole: (userTypeId: string) => void;
  onDefaultUserTypeIdChange: (userTypeId: string) => void;
  onRoleCapChange: (userTypeId: string, field: keyof RoleCapsForm, value: string) => void;
}

export const ServicePricingMatrix: React.FC<ServicePricingMatrixProps> = ({
  options,
  activeRoleIds,
  defaultUserTypeId,
  roleCaps,
  onCellChange,
  onAddRole,
  onRemoveRole,
  onDefaultUserTypeIdChange,
  onRoleCapChange,
}) => {
  const { userTypes } = useUserTypes();
  const [roleToAdd, setRoleToAdd] = useState('');
  const [previewRoleId, setPreviewRoleId] = useState('');

  const nameFor = (userTypeId: string) =>
    userTypes.find((ut) => String(ut.id) === userTypeId)?.name || `Role #${userTypeId}`;

  const availableToAdd = useMemo(
    () => userTypes.filter((ut) => !activeRoleIds.includes(String(ut.id))),
    [userTypes, activeRoleIds]
  );

  // Mirrors functions/booking/pricing.py::_role_price's fallback chain, for
  // the read-only "view prices as role" preview — never sent anywhere, just
  // rendered locally so an admin can sanity-check a role's rates.
  const resolvedPriceFor = (option: PricingMatrixOptionRow, roleId: string): { value: string; source: string } => {
    if (roleId && option.pricesByRole[roleId] !== undefined && option.pricesByRole[roleId] !== '') {
      return { value: option.pricesByRole[roleId], source: 'role rate' };
    }
    if (
      defaultUserTypeId &&
      option.pricesByRole[defaultUserTypeId] !== undefined &&
      option.pricesByRole[defaultUserTypeId] !== ''
    ) {
      return { value: option.pricesByRole[defaultUserTypeId], source: 'default role rate' };
    }
    const hasAnyRolePrice = Object.values(option.pricesByRole).some((v) => v !== undefined && v !== '');
    if (hasAnyRolePrice) {
      return { value: '—', source: 'not sold to this role' };
    }
    return { value: option.flatPrice || '0', source: 'flat rate' };
  };

  return (
    <div className="text-sm">
      <p className="text-xs mb-3" style={{ color: 'var(--ink-500)' }}>
        Optional. Leave a cell empty to keep that guest type at its flat price for every
        role, or to block a role from buying it once other roles have their own rate.
      </p>

      {activeRoleIds.length === 0 ? (
        <p className="text-xs italic" style={{ color: 'var(--ink-400)' }}>
          No role-specific prices yet — everyone pays the flat price above.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid var(--line)' }}>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                <th className="text-left font-medium px-2 py-2" style={{ color: 'var(--ink-500)' }}>
                  Guest type
                </th>
                {activeRoleIds.map((roleId) => (
                  <th key={roleId} className="text-left font-medium px-2 py-2" style={{ color: 'var(--ink-700)' }}>
                    <div className="flex items-center gap-1">
                      <span>{nameFor(roleId)}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${nameFor(roleId)} column`}
                        onClick={() => onRemoveRole(roleId)}
                        className="rounded hover:opacity-70"
                        style={{ color: 'var(--ink-400)' }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                    <label className="flex items-center gap-1 mt-1 font-normal" style={{ color: 'var(--ink-500)' }}>
                      <input
                        type="radio"
                        name="pricing-default-role"
                        checked={defaultUserTypeId === roleId}
                        onChange={() => onDefaultUserTypeIdChange(roleId)}
                      />
                      default
                    </label>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {options.map((option) => (
                <tr key={option.index} style={{ borderTop: '1px solid var(--line-subtle)' }}>
                  <td className="px-2 py-2 align-top">
                    <div style={{ color: 'var(--ink-900)' }}>{option.label || option.id || '(unnamed)'}</div>
                    <div className="text-[11px]" style={{ color: 'var(--ink-400)' }}>
                      flat: {option.flatPrice || '0'}
                    </div>
                  </td>
                  {activeRoleIds.map((roleId) => (
                    <td key={roleId} className="px-2 py-2 align-top">
                      <input
                        type="number"
                        step="0.01"
                        value={option.pricesByRole[roleId] ?? ''}
                        placeholder="—"
                        onChange={(e) => onCellChange(option.index, roleId, e.target.value)}
                        className="w-20 rounded px-1.5 py-1 text-xs"
                        style={{ border: '1px solid var(--line)' }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
              <tr style={{ borderTop: '1px solid var(--line-subtle)' }}>
                <td className="px-2 py-2 align-top text-[11px]" style={{ color: 'var(--ink-500)' }}>
                  Caps at that role's rate
                </td>
                {activeRoleIds.map((roleId) => (
                  <td key={roleId} className="px-2 py-2 align-top">
                    <div className="flex flex-col gap-1">
                      <input
                        type="number"
                        min={0}
                        value={roleCaps[roleId]?.max_qty_per_booking ?? ''}
                        placeholder="max / booking"
                        onChange={(e) => onRoleCapChange(roleId, 'max_qty_per_booking', e.target.value)}
                        className="w-24 rounded px-1.5 py-1 text-[11px]"
                        style={{ border: '1px solid var(--line)' }}
                      />
                      <input
                        type="number"
                        min={0}
                        value={roleCaps[roleId]?.max_qty_per_day ?? ''}
                        placeholder="max / day"
                        onChange={(e) => onRoleCapChange(roleId, 'max_qty_per_day', e.target.value)}
                        className="w-24 rounded px-1.5 py-1 text-[11px]"
                        style={{ border: '1px solid var(--line)' }}
                      />
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-2 mt-3">
        <select
          value={roleToAdd}
          onChange={(e) => setRoleToAdd(e.target.value)}
          className="rounded px-2 py-1.5 text-xs"
          style={{ border: '1px solid var(--line)' }}
        >
          <option value="">Add a role…</option>
          {availableToAdd.map((ut) => (
            <option key={ut.id} value={ut.id}>
              {ut.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!roleToAdd}
          onClick={() => {
            if (!roleToAdd) return;
            onAddRole(roleToAdd);
            setRoleToAdd('');
          }}
          className="btn"
          style={{ opacity: roleToAdd ? 1 : 0.5 }}
        >
          <Plus size={12} />
          Add role column
        </button>
      </div>

      {activeRoleIds.length > 0 && (
        <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--line-subtle)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Eye size={12} style={{ color: 'var(--ink-500)' }} />
            <span className="text-xs font-medium" style={{ color: 'var(--ink-700)' }}>
              View prices as role
            </span>
            <select
              value={previewRoleId}
              onChange={(e) => setPreviewRoleId(e.target.value)}
              className="rounded px-2 py-1 text-xs"
              style={{ border: '1px solid var(--line)' }}
            >
              <option value="">Everyone else</option>
              {activeRoleIds.map((roleId) => (
                <option key={roleId} value={roleId}>
                  {nameFor(roleId)}
                </option>
              ))}
            </select>
          </div>
          <ul className="text-xs space-y-1">
            {options.map((option) => {
              const resolved = resolvedPriceFor(option, previewRoleId);
              return (
                <li key={option.index} className="flex items-center justify-between">
                  <span style={{ color: 'var(--ink-700)' }}>{option.label || option.id}</span>
                  <span style={{ color: 'var(--ink-500)' }}>
                    {resolved.value} <span className="text-[11px]">({resolved.source})</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
